// frontend/src/features/api/instructors.ts
import {
  InstructorProfile,
  InstructorProfileListItem,
  InstructorProfileCreate,
  InstructorProfileUpdate,
  InstructorProfilePublic,
  UserSearchResult
} from '../instructors/types';
import {
  InstructorInvitation,
  InvitationList,
  InvitationCreate,
  InvitationStatus
} from '../../types/invitation';

const API_BASE = '/api/v1';

// Request options for authenticated requests (uses HTTP-only cookies)
const authRequestOptions = {
  credentials: 'include' as RequestCredentials,
  headers: {
    'Content-Type': 'application/json',
  },
};

// Admin Instructor API functions
export const instructorAdminApi = {
  // Get all instructors (admin only)
  getInstructors: async (page: number = 1, limit: number = 20, search?: string): Promise<{ instructors: InstructorProfileListItem[], total: number }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });
    
    const response = await fetch(`${API_BASE}/admin/instructors?${params}`, {
      ...authRequestOptions,
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch instructors');
    }
    
    return response.json();
  },

  // Get single instructor (admin)
  getInstructor: async (id: number): Promise<InstructorProfile> => {
    const response = await fetch(`${API_BASE}/admin/instructors/${id}`, {
      ...authRequestOptions,
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch instructor');
    }
    
    return response.json();
  },

  // Create instructor
  createInstructor: async (data: InstructorProfileCreate): Promise<InstructorProfile> => {
    const response = await fetch(`${API_BASE}/admin/instructors`, {
      method: 'POST',
      ...authRequestOptions,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create instructor');
    }
    
    return response.json();
  },

  // Update instructor
  updateInstructor: async (id: number, data: InstructorProfileUpdate): Promise<InstructorProfile> => {
    const response = await fetch(`${API_BASE}/admin/instructors/${id}`, {
      method: 'PUT',
      ...authRequestOptions,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update instructor');
    }
    
    return response.json();
  },

  // Delete instructor
  deleteInstructor: async (id: number): Promise<void> => {
    const response = await fetch(`${API_BASE}/admin/instructors/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete instructor');
    }
  },

  // Upload profile picture
  uploadProfilePicture: async (instructorId: number, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image_file', file);

    const response = await fetch(`${API_BASE}/instructors/${instructorId}/profile-picture/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload profile picture');
    }
    
    return response.json();
  },

  // Search users for instructor creation
  searchUsers: async (query: string, limit: number = 10): Promise<UserSearchResult[]> => {
    const params = new URLSearchParams({ 
      q: query,
      limit: limit.toString()
    });
    
    const response = await fetch(`${API_BASE}/admin/users/search?${params}`, {
      ...authRequestOptions,
    });
    
    if (!response.ok) {
      throw new Error('Failed to search users');
    }
    
    return response.json();
  },

  // Invitation management (admin only)
  createInvitation: async (invitationData: InvitationCreate): Promise<InstructorInvitation> => {
    const response = await fetch(`${API_BASE}/admin/instructors/invitations`, {
      method: 'POST',
      ...authRequestOptions,
      body: JSON.stringify(invitationData),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send invitation');
    }
    
    return response.json();
  },

  getInvitations: async (page: number = 1, limit: number = 50, status?: InvitationStatus): Promise<InvitationList> => {
    const params = new URLSearchParams({
      skip: ((page - 1) * limit).toString(),
      limit: limit.toString(),
      ...(status && { status }),
    });
    
    const response = await fetch(`${API_BASE}/admin/instructors/invitations?${params}`, {
      ...authRequestOptions,
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch invitations');
    }
    
    return response.json();
  },

  revokeInvitation: async (invitationId: number): Promise<void> => {
    const response = await fetch(`${API_BASE}/admin/instructors/invitations/${invitationId}/revoke`, {
      method: 'POST',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to revoke invitation');
    }
  },
};

// Public Instructor API functions
export const instructorPublicApi = {
  // Get public instructor profile
  getPublicInstructor: async (id: number): Promise<InstructorProfilePublic> => {
    const response = await fetch(`${API_BASE}/instructors/${id}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch instructor profile');
    }
    
    return response.json();
  },

  // Validate invitation token for registration landing page
  validateInvitationToken: async (token: string): Promise<{
    valid: boolean;
    email?: string;
    expires_at?: string;
    message?: string;
  }> => {
    const response = await fetch(`${API_BASE}/instructors/register?token=${encodeURIComponent(token)}`);
    
    if (!response.ok) {
      throw new Error('Failed to validate invitation token');
    }
    
    return response.json();
  },

  // Register existing user as instructor using invitation token
  registerExistingUserAsInstructor: async (data: {
    token: string;
    display_name: string;
    bio?: string;
  }): Promise<{
    message: string;
    instructor_id: number;
    user_id: number;
  }> => {
    const response = await fetch(`${API_BASE}/instructors/register-existing-user`, {
      method: 'POST',
      ...authRequestOptions,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to register as instructor');
    }
    
    return response.json();
  },
};

// Instructor Self-Service API functions
export const instructorSelfServiceApi = {
  // Get current instructor's profile
  getInstructorProfile: async (): Promise<InstructorProfile> => {
    const response = await fetch(`${API_BASE}/instructors/profile`, {
      ...authRequestOptions,
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch instructor profile');
    }
    
    return response.json();
  },

  // Update current instructor's profile
  updateInstructorProfile: async (data: InstructorProfileUpdate): Promise<InstructorProfilePublic> => {
    const response = await fetch(`${API_BASE}/instructors/profile`, {
      method: 'PUT',
      ...authRequestOptions,
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update instructor profile');
    }
    
    return response.json();
  },

  // Upload profile picture for current instructor
  uploadInstructorProfilePicture: async (instructorId: number, file: File): Promise<{ profile_picture_url: string }> => {
    const formData = new FormData();
    formData.append('image_file', file);

    const response = await fetch(`${API_BASE}/instructors/${instructorId}/profile-picture/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload profile picture');
    }
    
    const result = await response.json();
    return { profile_picture_url: result.url };
  },
};

// Export individual functions for easier imports
export const { getInstructors, createInstructor, updateInstructor, deleteInstructor, getInvitations, createInvitation, revokeInvitation } = instructorAdminApi;
export const { getPublicInstructor } = instructorPublicApi;
export const { 
  getInstructorProfile, 
  updateInstructorProfile, 
  uploadInstructorProfilePicture
} = instructorSelfServiceApi;

// Default export combining all APIs
export const api = {
  ...instructorAdminApi,
  ...instructorPublicApi,
  ...instructorSelfServiceApi,
};
